import { resolveConfig, type ObservabilityConfig } from "./config";
import { newSpanId, newTraceId } from "./ids";
import type { AsyncLocalStorage } from "node:async_hooks";

/**
 * Log severity, aligned with Axiom / OpenTelemetry log levels.
 */
export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogContext {
  [key: string]: unknown;
}

export interface TelemetryEvent {
  /** @t (Axiom recognized timestamp field). */
  "@t": string;
  /** @traceId — W3C trace id used to correlate a whole request/journey. */
  "@traceId": string;
  /** @spanId — W3C span id for this individual span/event. */
  "@spanId": string;
  /** W3C span id of the enclosing span, when this event is emitted inside one. */
  parentSpanId?: string;
  level: LogLevel;
  service: string;
  version: string;
  environment: string;
  message: string;
  context?: LogContext;
}

export interface ActiveSpan {
  /** W3C trace id for the whole trace. */
  traceId: string;
  /** W3C span id for this span. */
  spanId: string;
  /** W3C span id of the enclosing span, when nested. */
  parentSpanId?: string;
  name: string;
  startedAt: number;
  end: (context?: LogContext) => void;
  setStatus: (ok: boolean, message?: string) => void;
}

/** W3C context of the currently active span (for traceparent propagation). */
export interface TraceContext {
  traceId: string;
  spanId: string;
}

export interface Telemetry {
  readonly config: ObservabilityConfig;
  log: (level: LogLevel, message: string, context?: LogContext) => void;
  debug: (message: string, context?: LogContext) => void;
  info: (message: string, context?: LogContext) => void;
  warn: (message: string, context?: LogContext) => void;
  error: (message: string, context?: LogContext) => void;
  /** Create a child span; call `end()` when done. */
  startSpan: (name: string, parentContext?: LogContext) => ActiveSpan;
  /** Run `fn` inside a span, exporting its result. */
  withSpan: <T>(
    name: string,
    fn: () => Promise<T> | T,
    context?: LogContext,
  ) => Promise<T>;
  /**
   * The active span's W3C `{traceId, spanId}` context, or `null` when no
   * span is active on the current async chain. Used to derive outbound
   * `traceparent` headers so the receiving service continues THIS trace.
   */
  currentTraceContext: () => TraceContext | null;
  /** Ship any pending events now, without disturbing periodic shipping. */
  flush: () => Promise<void>;
  /** Stop periodic shipping and flush any pending events (teardown). */
  shutdown: () => Promise<void>;
}

interface PendingEvent extends TelemetryEvent {
  _kind: "log" | "span-end";
  durationMs?: number;
  ok?: boolean;
  statusMessage?: string;
}

/** One entry in the active span stack. */
interface SpanFrame {
  traceId: string;
  spanId: string;
  parentSpanId?: string;
}

/**
 * The `AsyncLocalStorage` implementation, or `undefined` outside Node/Bun
 * (browsers have no `process.versions.node` and no `async_hooks`). Loaded
 * lazily and guarded so it never throws in the browser.
 *
 * Uses `process.getBuiltinModule` — the ESM-safe way to load a Node builtin
 * synchronously (Node ≥ 22.3, Bun ≥ 1.1). `require` cannot be used here: this
 * is an ESM module, where `require` is undefined, which silently disabled
 * async-local span attribution under plain Node. Runtimes without
 * `getBuiltinModule` degrade gracefully to the global span-stack fallback.
 */
const AsyncLocalStorageImpl: (new <T>() => AsyncLocalStorage<T>) | undefined =
  (() => {
    try {
      const proc = (
        globalThis as {
          process?: {
            versions?: { node?: string };
            getBuiltinModule?: (id: string) => unknown;
          };
        }
      ).process;
      if (!proc?.versions?.node) return undefined;
      if (typeof proc.getBuiltinModule !== "function") return undefined;
      const mod = proc.getBuiltinModule("node:async_hooks") as
        { AsyncLocalStorage: new <T>() => AsyncLocalStorage<T> } | undefined;
      return mod?.AsyncLocalStorage;
    } catch {
      return undefined;
    }
  })();

/**
 * A lightweight, self-contained telemetry client.
 *
 * - Always captures structured events in-memory (and to console in dev).
 * - When Axiom credentials are configured, batches events and ships them to the
 *   Axiom ingest endpoint via a plain `fetch`.
 * - When credentials are absent, `enabled` is false and nothing is shipped —
 *   the app keeps working and events are simply logged to the console.
 */
export function createTelemetry(
  service: string,
  version = "0.1.0",
  overrides: Parameters<typeof resolveConfig>[2] = {},
): Telemetry {
  const config = resolveConfig(service, version, overrides);
  const pending: PendingEvent[] = [];
  let flushTimer: ReturnType<typeof setInterval> | undefined;
  let flushing = false;
  let inFlight: Promise<void> = Promise.resolve();

  // Backpressure bound: if Axiom is unreachable for a long time, drop the
  // oldest events rather than growing memory without limit.
  const MAX_PENDING = 1000;

  // Browsers reject `keepalive` request bodies larger than 64 KiB, so chunk
  // oversized unload batches a little under the cap.
  const KEEPALIVE_CHUNK_BYTES = 60_000;

  // Exponential backoff for automatic shipping after failures: 2s -> 4s ->
  // 8s -> ... capped at 60s. Reset on a successful ship. Manual `flush()`
  // bypasses the backoff gate.
  const BACKOFF_BASE_MS = 2_000;
  const BACKOFF_MAX_MS = 60_000;
  let consecutiveFailures = 0;
  let nextShipAt = 0;

  const backoffDelayMs = () =>
    Math.min(
      BACKOFF_MAX_MS,
      BACKOFF_BASE_MS * 2 ** Math.min(consecutiveFailures, 6),
    );

  /** Enforce the pending cap by dropping the oldest events. */
  const capPending = () => {
    if (pending.length > MAX_PENDING) {
      pending.splice(0, pending.length - MAX_PENDING);
    }
  };

  /** Queue a new event, dropping the oldest when over the cap. */
  const queue = (event: PendingEvent) => {
    pending.push(event);
    capPending();
    if (config.console) writeToConsole(event);
  };

  /**
   * Re-queue a failed batch at the front of the queue (it holds the newest
   * events), enforcing the same `MAX_PENDING` cap as `queue()` by dropping
   * the oldest pending events.
   */
  const requeueBatch = (batch: PendingEvent[]) => {
    // A single batch larger than the cap keeps only its newest tail.
    const trimmed =
      batch.length > MAX_PENDING
        ? batch.slice(batch.length - MAX_PENDING)
        : batch;
    const overflow = trimmed.length + pending.length - MAX_PENDING;
    if (overflow > 0) {
      pending.splice(0, overflow);
    }
    pending.unshift(...trimmed);
  };

  const writeToConsole = (event: PendingEvent) => {
    const ctx = event.context ? ` ${JSON.stringify(event.context)}` : "";
    const line = `[${event.service}] ${event.level}: ${event.message}${ctx}`;
    if (event.level === "error") console.error(line);
    else if (event.level === "warn") console.warn(line);
    else if (event.level === "debug") console.debug(line);
    else console.info(line);
  };

  /**
   * Async-local span context. Under Node/Bun each async chain gets its own
   * span stack, so concurrent `withSpan` calls that interleave across `await`
   * attribute logs to their own span. Browsers keep the plain module-level
   * `spanStack` fallback below.
   */
  const asyncStorage =
    AsyncLocalStorageImpl !== undefined
      ? new AsyncLocalStorageImpl<SpanFrame[]>()
      : undefined;

  /**
   * Fallback span stack for browsers. `withSpan` pushes before running its
   * function and pops afterwards, so logs emitted inside a span share its
   * trace id. Limitation: this is a single global stack, so concurrent spans
   * that interleave across `await` boundaries may attribute logs to whichever
   * span pushed most recently.
   */
  const spanStack: SpanFrame[] = [];

  /** The active span stack: async-local under Node/Bun, global in browsers. */
  const activeStack = (): SpanFrame[] => asyncStorage?.getStore() ?? spanStack;

  const baseEvent = (
    level: LogLevel,
    message: string,
    context?: LogContext,
  ) => {
    const stack = activeStack();
    const active = stack[stack.length - 1];
    const traceId = active?.traceId ?? newTraceId();
    // Inside a span, the log belongs to that span (not a fresh one), and it
    // records the span's parent so the trace continues across the log.
    const spanId = active?.spanId ?? newSpanId();
    return {
      "@t": new Date().toISOString(),
      "@traceId": traceId,
      "@spanId": spanId,
      ...(active?.parentSpanId !== undefined
        ? { parentSpanId: active.parentSpanId }
        : {}),
      level,
      service: config.service,
      version: config.version,
      environment: config.environment,
      message,
      context,
    } satisfies TelemetryEvent;
  };

  const log = (level: LogLevel, message: string, context?: LogContext) => {
    queue({ ...baseEvent(level, message, context), _kind: "log" });
  };

  const startSpan = (name: string, parentContext?: LogContext): ActiveSpan => {
    // Default to the active frame so a nested span inherits the in-flight
    // trace instead of starting an unrelated one.
    const activeSpans = activeStack();
    const active = activeSpans[activeSpans.length - 1];
    const traceId = String(
      parentContext?.["@traceId"] ?? active?.traceId ?? newTraceId(),
    );
    const parentId = parentContext?.["@spanId"]
      ? String(parentContext["@spanId"])
      : active?.spanId;
    const spanId = newSpanId();
    const startedAt = Date.now();
    let statusOk = true;
    let statusMessage: string | undefined;

    const end = (context?: LogContext) => {
      queue({
        ...baseEvent("info", `span:${name}`, {
          ...(parentContext ?? {}),
          ...(context ?? {}),
        }),
        _kind: "span-end",
        "@traceId": traceId,
        "@spanId": spanId,
        parentSpanId: parentId,
        durationMs: Date.now() - startedAt,
        ok: statusOk,
        statusMessage,
      });
    };

    return {
      traceId,
      spanId,
      parentSpanId: parentId,
      name,
      startedAt,
      end,
      setStatus: (ok, message) => {
        statusOk = ok;
        statusMessage = message;
      },
    };
  };

  const withSpan = async <T>(
    name: string,
    fn: () => Promise<T> | T,
    context?: LogContext,
  ): Promise<T> => {
    const span = startSpan(name, context);
    const frame: SpanFrame = {
      traceId: span.traceId,
      spanId: span.spanId,
      parentSpanId: span.parentSpanId,
    };

    const run = async (): Promise<T> => {
      try {
        const result = await fn();
        span.end({ ok: true });
        return result;
      } catch (err) {
        span.setStatus(false, err instanceof Error ? err.message : String(err));
        span.end({ error: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    };

    if (asyncStorage) {
      // Copy the parent stack: sibling async chains must not observe this
      // span, and this chain must not observe spans pushed later on them.
      const stack = asyncStorage.getStore() ?? [];
      return asyncStorage.run([...stack, frame], run);
    }
    spanStack.push(frame);
    try {
      return await run();
    } finally {
      spanStack.pop();
    }
  };

  /** The active span's W3C context, or `null` outside any span. */
  const currentTraceContext = (): TraceContext | null => {
    const stack = activeStack();
    const frame = stack[stack.length - 1];
    return frame ? { traceId: frame.traceId, spanId: frame.spanId } : null;
  };

  const markFailure = () => {
    consecutiveFailures += 1;
    nextShipAt = Date.now() + backoffDelayMs();
  };

  const markSuccess = () => {
    consecutiveFailures = 0;
    nextShipAt = 0;
  };

  /**
   * Ship pending events to Axiom.
   *
   * `force` bypasses the failure backoff gate (used by manual flush and
   * unload handlers); `keepalive` marks the request as one the browser should
   * complete even if the page is unloading.
   *
   * A force flush never silently no-ops while a ship is in flight: it waits
   * for the in-flight request, then ships whatever remains (pending may have
   * refilled or been requeued). Without this, a manual flush overlapping the
   * periodic ship would return immediately and lose the flush's purpose.
   *
   * The page-unload path must NOT wait, though — see `unloadFlush` below,
   * which the teardown handlers call instead of this.
   */
  const ship = async (force = false, keepalive = false): Promise<void> => {
    if (!config.enabled || !config.ingestUrl || !config.token) return;
    if (flushing) {
      if (!force) return;
      await inFlight;
    }
    if (!force && Date.now() < nextShipAt) return;
    if (pending.length === 0) return;
    flushing = true;
    inFlight = shipNow(keepalive).finally(() => {
      flushing = false;
    });
    await inFlight;
  };

  /** Ship all pending events. Callers guarantee single-flight via `ship`. */
  const shipNow = async (keepalive = false): Promise<void> => {
    if (!config.ingestUrl) return;
    const batch = pending.splice(0, pending.length);
    await sendBatch(batch, keepalive);
  };

  /**
   * Send a batch to the ingest endpoint, requeueing it on failure. Chunks
   * oversized keepalive bodies under the browser's 64 KiB cap. Does not touch
   * `pending` beyond failure requeues — the batch is owned by the caller.
   */
  const sendBatch = async (
    batch: PendingEvent[],
    keepalive: boolean,
  ): Promise<void> => {
    const ingestUrl = config.ingestUrl;
    if (!ingestUrl || batch.length === 0) return;
    try {
      // `User-Agent` is a forbidden header name in browsers: setting it there
      // is silently ignored, so only send it from non-browser runtimes.
      const headers: Record<string, string> = {
        Authorization: `Bearer ${config.token}`,
        "Content-Type": "application/json",
      };
      if (typeof window === "undefined") {
        headers["User-Agent"] = `ito-observability/${config.version}`;
      }
      const send = async (body: string) => {
        // Timeout each ingest call so a hung connection can't stall flushing.
        const signal =
          typeof AbortSignal !== "undefined" &&
          typeof AbortSignal.timeout === "function"
            ? AbortSignal.timeout(10_000)
            : undefined;
        return fetch(ingestUrl, {
          method: "POST",
          headers,
          body,
          signal,
          keepalive,
        });
      };

      const body = JSON.stringify(batch);
      // Browsers cap `keepalive` request bodies at 64 KiB and reject larger
      // ones outright — an unload batch of up to MAX_PENDING events would
      // never make it. Chunk oversized keepalive bodies under the cap. The cap
      // is in BYTES, so measure the encoded body, not `body.length` (UTF-16
      // code units) — a non-ASCII payload of 40k chars can exceed 64 KiB.
      const bodyBytes =
        typeof Blob !== "undefined"
          ? new Blob([body]).size
          : new TextEncoder().encode(body).length;
      const chunks =
        keepalive && bodyBytes > KEEPALIVE_CHUNK_BYTES
          ? Math.ceil(bodyBytes / KEEPALIVE_CHUNK_BYTES)
          : 1;
      if (chunks === 1) {
        try {
          const res = await send(body);
          if (!res.ok) {
            console.warn(
              `[observability] Axiom ingest failed (${res.status}): ${await res.text()}`,
            );
            requeueBatch(batch);
            markFailure();
          } else {
            markSuccess();
          }
        } catch (err) {
          console.warn(
            "[observability] Axiom ingest error:",
            err instanceof Error ? err.message : String(err),
          );
          requeueBatch(batch);
          markFailure();
        }
        return;
      }

      const size = Math.max(1, Math.ceil(batch.length / chunks));
      // Failed chunks are collected and requeued as ONE batch so they keep
      // their chronological order in the queue. Requeueing each chunk as it
      // fails (front-insertion per chunk) would reverse them — the newest
      // chunk would sit in front of the older ones.
      const failed: PendingEvent[] = [];
      for (let i = 0; i < batch.length; i += size) {
        const chunk = batch.slice(i, i + size);
        try {
          const res = await send(JSON.stringify(chunk));
          if (!res.ok) {
            console.warn(`[observability] Axiom ingest failed (${res.status})`);
            failed.push(...chunk);
            markFailure();
          } else {
            markSuccess();
          }
        } catch (err) {
          console.warn(
            "[observability] Axiom ingest error:",
            err instanceof Error ? err.message : String(err),
          );
          failed.push(...chunk);
          markFailure();
        }
      }
      if (failed.length > 0) requeueBatch(failed);
    } catch (err) {
      // Serialization failure — requeue so nothing is silently lost.
      console.warn(
        "[observability] Axiom ingest error:",
        err instanceof Error ? err.message : String(err),
      );
      requeueBatch(batch);
      markFailure();
    }
  };

  /**
   * Flush pending events on page hide/unload, so short browser sessions
   * don't lose telemetry. Unlike `ship(true, ...)`, this never awaits an
   * in-flight periodic ship: the page may be torn down before that request
   * completes, and the final events would never be sent. Instead the current
   * pending queue is snapshotted right away and sent with a `keepalive`
   * fetch — no overlap or duplication, because `shipNow` removed the
   * in-flight batch from `pending` when the ship started. The in-flight
   * ship's `flushing`/`inFlight` bookkeeping is left untouched; if it later
   * fails, its requeue lands after teardown, which is harmless (and if the
   * page survived in a bfcache, the requeued events simply ship next time).
   */
  const unloadFlush = () => {
    if (!config.enabled || !config.ingestUrl || !config.token) return;
    if (flushing) {
      const snapshot = pending.splice(0, pending.length);
      if (snapshot.length > 0) void sendBatch(snapshot, true);
      return;
    }
    void ship(true, true);
  };

  /**
   * Register the unload flush. Browsers only; no-ops in Node.
   */
  const registerUnloadFlush = () => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;
    window.addEventListener("pagehide", () => unloadFlush());
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") unloadFlush();
    });
  };

  if (config.enabled) {
    flushTimer = setInterval(ship, config.flushIntervalMs);
    if (typeof flushTimer.unref === "function") flushTimer.unref();
    registerUnloadFlush();
  }

  // Ship now but keep periodic shipping alive — `flush()` is safe to call at
  // any time. Use `shutdown()` for final teardown.
  const flush = async () => {
    await ship(true);
  };

  const shutdown = async () => {
    if (flushTimer) {
      clearInterval(flushTimer);
      flushTimer = undefined;
    }
    await ship(true, true);
  };

  return {
    config,
    log,
    debug: (message, context) => log("debug", message, context),
    info: (message, context) => log("info", message, context),
    warn: (message, context) => log("warn", message, context),
    error: (message, context) => log("error", message, context),
    startSpan,
    withSpan,
    currentTraceContext,
    flush,
    shutdown,
  };
}

export type { ObservabilityConfig };
